import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Image, StyleSheet } from 'react-native';
import { CoachPanelStudent } from './types';
import {AwardIcon} from './AwardIcon';
import StarAsset from '@/Assets/Recognition/Star.svg';
import CrownAsset from '@/Assets/Recognition/Crown.svg';

const C = {
  bg: '#FAF6EB', card: '#FFFFFF', brown: '#4D3F37',
  brownLight: '#4D3F37CC', gold: '#FACF47', goldLight: '#FEF2B5',
  aqua: '#3DAB9E', coral: '#E17B21', purple: '#937ADB',
  border: 'rgba(38,35,34,0.18)', borderStrong: 'rgba(38,35,34,0.32)',
};

const AWARDS = [
  { key: 'good_try',      label: 'Good Try',      color: C.aqua,   tint: '#21CDBD29' },
  { key: 'great_effort',  label: 'Great Effort',  color: C.coral,  tint: '#FF854B29' },
  { key: 'good_thinking', label: 'Good Thinking', color: C.gold,   tint: C.goldLight },
  { key: 'focused',       label: 'Focused',       color: C.purple, tint: '#937ADB26' },
  { key: 'team_player',   label: 'Team Player',   color: C.brown,  tint: '#4D3F3712' },
];

const STATUS_COLOR: Record<string, string> = {
  Active: C.aqua, Quiet: C.gold, Inactive: C.coral,
};

interface CleanCoachDrawerProps {
  students: CoachPanelStudent[];
  manualCount: number;
  mysteryCount: number;
  onAward: (studentId: number, awardType: string) => void;
  onClose: () => void;
}

export function CleanCoachDrawer({
  students, manualCount, mysteryCount, onAward, onClose,
}: CleanCoachDrawerProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [pending, setPending] = useState<{ studentId: number; awardKey: string } | null>(null);

  const notYet = students.filter(s => s.coach_awards === 0);
  const sorted = [...students].sort((a, b) => {
    const order: Record<string, number> = { Quiet: 0, Inactive: 1, Active: 2 };
    return (order[a.status] ?? 0) - (order[b.status] ?? 0) || b.stars - a.stars;
  });

  function initials(name: string) {
    return name.split(' ').map(p => p[0]).slice(0, 2).join('');
  }

  return (
    <View style={d.container}>
      {/* Header */}
      <View style={d.header}>
        <View style={{ flex: 1 }}>
          <Text style={d.headerTitle}>Class Panel</Text>
          <Text style={d.headerMeta}>{students.length} students · {manualCount}/15 awards given</Text>
        </View>
        <TouchableOpacity onPress={onClose} style={d.closeBtn}>
          <Text style={d.closeIcon}>×</Text>
        </TouchableOpacity>
      </View>

      {/* Not-yet-recognised nudge */}
      {notYet.length > 0 && (
        <View style={d.nudge}>
          <Text style={d.nudgeDot}>● </Text>
          <Text style={d.nudgeText}>
            <Text style={d.nudgeBold}>{notYet.length} not yet recognised</Text>
            {' · '}{notYet.map(s => s.name.split(' ')[0]).join(', ')}
          </Text>
        </View>
      )}

      {/* Mystery count indicator (read-only) */}
      <View style={d.mysteryRow}>
        <Image source={CrownAsset} style={{ width: 20, height: 20 }} resizeMode="contain" />
        <View style={{ flex: 1 }}>
          <Text style={d.mysteryLabel}>Mystery Bonus: {mysteryCount}/2 used</Text>
          <Text style={d.mysterySub}>Toggle on a puzzle at creation. Correct solve = 3× stars.</Text>
        </View>
      </View>

      {/* Student list */}
      <ScrollView style={d.list}>
        {sorted.map((s, idx) => {
          const isExpanded = expandedId === s.user_id;
          const isLast = idx === sorted.length - 1;
          const statusColor = STATUS_COLOR[s.status] ?? C.brownLight;

          return (
            <View key={s.user_id} style={[d.row, !isLast && d.rowBorder, isExpanded && d.rowExpanded]}>
              <TouchableOpacity
                onPress={() => { setExpandedId(isExpanded ? null : s.user_id); setPending(null); }}
                style={d.rowBtn}
              >
                <View style={d.avatar}>
                  <Text style={d.avatarText}>{initials(s.name)}</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={d.studentName}>{s.name}</Text>
                  <Text style={d.studentMeta}>
                    {s.attempts} attempts · {s.correct} correct ·{' '}
                    <Text style={{ color: statusColor, fontWeight: '600' }}>{s.status}</Text>
                  </Text>
                </View>
                <View style={[d.starsPill, s.stars === 0 ? d.starsPillEmpty : d.starsPillFull]}>
                  <Image source={StarAsset} style={{ width: 14, height: 14 }} resizeMode="contain" />
                  <Text style={[d.starsNum, { color: s.stars === 0 ? C.brownLight : C.brown }]}>{s.stars}</Text>
                </View>
                <Text style={[d.chevron, isExpanded && d.chevronExpanded]}>›</Text>
              </TouchableOpacity>

              {isExpanded && (
                <View style={d.awardSection}>
                  {pending && pending.studentId === s.user_id ? (() => {
                    const aw = AWARDS.find(a => a.key === pending.awardKey)!;
                    return (
                      <View style={[d.confirmBox, { borderColor: aw.color }]}>
                        <View style={d.confirmHeader}>
                          <View style={[d.confirmIcon, { backgroundColor: aw.tint }]}>
                            <AwardIcon awardKey={aw.key} size={26} color={aw.color} />
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text style={d.confirmTitle}>
                              Give <Text style={{ color: aw.color }}>{aw.label}</Text> to {s.name.split(' ')[0]}?
                            </Text>
                            <Text style={d.confirmSub}>Adds +1 ⭐ and shares the badge with the class.</Text>
                          </View>
                        </View>
                        <View style={d.confirmBtns}>
                          <TouchableOpacity onPress={() => setPending(null)} style={d.btnNo}>
                            <Text style={d.btnNoText}>No</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => { onAward(s.user_id, pending.awardKey); setPending(null); setExpandedId(null); }}
                            style={[d.btnYes, { backgroundColor: aw.color }]}
                          >
                            <Text style={d.btnYesText}>Yes, send</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    );
                  })() : (
                    <View style={d.awardGrid}>
                      {AWARDS.map(a => (
                        <TouchableOpacity
                          key={a.key}
                          onPress={() => setPending({ studentId: s.user_id, awardKey: a.key })}
                          style={[d.awardBtn, { borderLeftColor: a.color }]}
                        >
                          <AwardIcon awardKey={a.key} size={18} color={a.color} />
                          <Text style={d.awardBtnLabel}>{a.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

const d = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.card },
  header: { flexDirection: 'row', alignItems: 'flex-start', padding: 16, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: C.border },
  headerTitle: { fontSize: 16, fontWeight: '700', color: C.brown },
  headerMeta: { fontSize: 12, fontWeight: '500', color: C.brownLight, marginTop: 3 },
  closeBtn: { width: 28, height: 28, alignItems: 'center', justifyContent: 'center' },
  closeIcon: { fontSize: 26, color: C.brownLight, lineHeight: 28 },
  nudge: { flexDirection: 'row', alignItems: 'flex-start', padding: 8, paddingHorizontal: 12, backgroundColor: '#FFFBF0', borderBottomWidth: 1, borderBottomColor: C.coral + '33' },
  nudgeDot: { fontSize: 8, color: C.coral, lineHeight: 18 },
  nudgeText: { flex: 1, fontSize: 12, color: C.brown },
  nudgeBold: { fontWeight: '700' },
  mysteryRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderBottomWidth: 1, borderBottomColor: C.border },
  mysteryLabel: { fontSize: 14, fontWeight: '800', color: C.brown },
  mysterySub: { fontSize: 11, fontWeight: '500', color: C.brownLight, marginTop: 1 },
  list: { flex: 1 },
  row: { backgroundColor: C.card },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: C.border },
  rowExpanded: { backgroundColor: C.bg },
  rowBtn: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 14, paddingHorizontal: 20 },
  avatar: { width: 40, height: 40, borderRadius: 20, backgroundColor: C.bg, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: 13, fontWeight: '700', color: C.brown },
  studentName: { fontSize: 15, fontWeight: '600', color: C.brown },
  studentMeta: { fontSize: 12, fontWeight: '500', color: C.brownLight, marginTop: 2 },
  starsPill: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, borderWidth: 1 },
  starsPillEmpty: { backgroundColor: C.bg, borderColor: C.border },
  starsPillFull: { backgroundColor: C.goldLight, borderColor: C.gold },
  starsNum: { fontSize: 13, fontWeight: '700' },
  chevron: { fontSize: 18, color: C.brownLight },
  chevronExpanded: { transform: [{ rotate: '90deg' }] },
  awardSection: { paddingHorizontal: 20, paddingBottom: 14 },
  awardGrid: { flexDirection: 'column', gap: 6 },
  awardBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 10, backgroundColor: C.card, borderRadius: 6, borderWidth: 1, borderColor: C.borderStrong, borderLeftWidth: 4, marginBottom: 4 },
  awardBtnLabel: { fontSize: 13, fontWeight: '700', color: C.brown },
  confirmBox: { borderWidth: 2, borderRadius: 8, padding: 12 },
  confirmHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  confirmIcon: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center' },
  confirmTitle: { fontSize: 13, fontWeight: '700', color: C.brown },
  confirmSub: { fontSize: 11, color: C.brownLight, marginTop: 2 },
  confirmBtns: { flexDirection: 'row', gap: 8 },
  btnNo: { flex: 1, height: 36, backgroundColor: C.card, borderWidth: 1, borderColor: C.borderStrong, borderRadius: 6, alignItems: 'center', justifyContent: 'center' },
  btnNoText: { fontSize: 13, fontWeight: '700', color: C.brown },
  btnYes: { flex: 1, height: 36, borderRadius: 6, alignItems: 'center', justifyContent: 'center' },
  btnYesText: { fontSize: 13, fontWeight: '800', color: '#fff' },
});
